/**
 * <liquid-tabs> — 液態黏滯風格的 tab 切換器（零依賴 Web Component）
 *
 * 使用方式：
 *   <script src="liquid-tabs.js"></script>
 *   <liquid-tabs
 *     tabs='[{"label":"照片","color":"#5B7CFA"},{"label":"音樂","color":"#F0524C"}]'
 *     size="76" gap="108"
 *     track-color="#B9C1D6" knob-color="#FFFFFF" label-color="#FFFFFF"
 *     selected="0">
 *   </liquid-tabs>
 *
 * 屬性（皆可省略）：
 *   tabs         JSON 陣列，每項 {label, color}。color 為選中時滑塊上的文字色。
 *   size         圓形槽直徑 px（預設 76），所有幾何與字級按比例縮放
 *   gap          相鄰圓心間距 px（預設 size×1.42）
 *   track-color  軌道顏色
 *   knob-color   滑塊顏色（漸層深端自動推導）
 *   label-color  軌道上未選中文字的顏色
 *   selected     目前選中的索引（會隨切換反映回屬性）
 *   swipe        存在時啟用滑動切換：拖曳滑塊跟手、放開吸附最近的槽，
 *                快速輕撥依方向前進一格（點擊行為不受影響）
 *
 * JS API：
 *   el.tabs = [...]            以屬性物件設定（優先於 tabs attribute）
 *   el.selected / el.select(i) 讀取或切換索引
 *   'change' 事件              detail = { index, tab }
 */
(() => {
  if (customElements.get("liquid-tabs")) return;

  class LiquidTabs extends HTMLElement {
    static get observedAttributes() {
      return ["tabs", "size", "gap", "track-color", "knob-color", "label-color", "selected", "swipe"];
    }

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this._current = 0;
      this._tabs = null;   // property 設定優先於 attribute
    }

    connectedCallback() { this._render(); }

    attributeChangedCallback(name, oldV, newV) {
      if (!this.shadowRoot || oldV === newV || !this._els) return;
      if (name === "selected") this.select(Number(newV));
      else this._render();
    }

    get tabs() {
      if (this._tabs) return this._tabs;
      try {
        const parsed = JSON.parse(this.getAttribute("tabs") || "null");
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch (e) { /* 落到預設值 */ }
      return [{ label: "取消", color: "#F0524C" }, { label: "確認", color: "#62C748" }];
    }
    set tabs(v) { this._tabs = v; this._render(); }

    get selected() { return this._current; }
    set selected(i) { this.select(Number(i)); }

    select(i) {
      const els = this._els;
      if (!els) return;
      i = Math.max(0, Math.min(els.slots.length - 1, i | 0));
      if (i === this._current) return;
      this._current = i;

      els.knob.style.transform  = `translateX(${i * this._gap}px)`;
      els.trail.style.transform = `translateX(${i * this._gap}px)`;

      els.switcher.classList.remove("switching");
      void els.switcher.offsetWidth;              // 重新觸發 squash 動畫
      els.switcher.classList.add("switching");

      els.slots.forEach((b, j) => b.setAttribute("aria-selected", j === i ? "true" : "false"));
      els.knobLabels.forEach((s, j) => s.classList.toggle("active", j === i));

      this.setAttribute("selected", String(i));   // 同索引時 select() 早退，不會迴圈
      this.dispatchEvent(new CustomEvent("change", {
        detail: { index: i, tab: this.tabs[i] },
        bubbles: true
      }));
    }

    _render() {
      const tabs  = this.tabs;
      const n     = tabs.length;
      const size  = Number(this.getAttribute("size")) || 76;
      const gap   = Number(this.getAttribute("gap"))  || Math.round(size * 1.42);
      const track = this.getAttribute("track-color") || "#B9C1D6";
      const knob  = this.getAttribute("knob-color")  || "#FFFFFF";
      const label = this.getAttribute("label-color") || "#FFFFFF";
      const sel   = Math.max(0, Math.min(n - 1, Number(this.getAttribute("selected")) || 0));

      // 幾何全部由 size / gap 推導
      const blur   = size * 0.184;                       // goo 模糊半徑
      const pad    = Math.ceil(blur * 3);                // 濾鏡層留白，避免模糊被裁切
      const bridge = Math.max(2, gap - size + 2);        // 橋接圓：讓中點長出細腰
      const trail  = size - Math.max(6, Math.round(size * 0.13));
      const font   = Math.max(11, Math.round(size * 0.21));
      const width  = size + gap * (n - 1);
      const r      = size / 2;

      this._gap = gap;
      this._current = sel;

      this.shadowRoot.innerHTML = `
        <style>
          :host{ display:inline-block; }
          *{ margin:0; padding:0; box-sizing:border-box; }

          .switcher{
            position:relative; width:${width}px; height:${size}px;
            user-select:none; -webkit-user-select:none;
          }

          .goo{
            position:absolute; inset:-${pad}px;
            filter:url(#goo);
            pointer-events:none;
          }
          .goo .blob{
            position:absolute; top:${pad}px;
            width:${size}px; height:${size}px;
            border-radius:50%;
            background:${track};
          }
          .goo .blob.trail{
            width:${trail}px; height:${trail}px;
            top:${pad + (size - trail) / 2}px;
            left:${pad + (size - trail) / 2}px;
            transition:transform .62s var(--lt-ease, cubic-bezier(.65,0,.25,1));
          }

          .slot{
            position:absolute; top:0;
            width:${size}px; height:${size}px;
            border:0; background:none; border-radius:50%;
            cursor:pointer;
            display:grid; place-items:center;
            font:inherit; color:${label};
            -webkit-tap-highlight-color:transparent;
          }
          .slot .label{
            font-size:${font}px; font-weight:700; letter-spacing:.08em;
            transition:opacity .3s;
          }
          .slot[aria-selected="true"] .label{ opacity:0; }
          .slot:focus-visible{
            outline:3px solid color-mix(in srgb, ${track} 45%, #5B7CFA);
            outline-offset:4px;
          }

          .knob{
            position:absolute; top:0; left:0;
            width:${size}px; height:${size}px;
            pointer-events:none;
            transition:transform .5s var(--lt-ease, cubic-bezier(.65,0,.25,1));
          }
          .knob-body{
            width:100%; height:100%;
            border-radius:50%;
            background:linear-gradient(150deg, ${knob} 20%, color-mix(in srgb, ${knob} 86%, #97A0BE) 90%);
            box-shadow:
              0 ${r * 0.37}px ${r * 0.68}px var(--lt-shadow-color, rgba(74,83,114,.28)),
              0 ${r * 0.1}px ${r * 0.21}px var(--lt-shadow-color-soft, rgba(74,83,114,.16)),
              inset 0 2px 3px rgba(255,255,255,.9);
            display:grid; place-items:center;
          }
          .knob-body > span{
            grid-area:1/1;
            font-size:${font}px; font-weight:700; letter-spacing:.08em;
            opacity:0; transform:scale(.5);
            transition:opacity .28s, transform .38s var(--lt-ease, cubic-bezier(.65,0,.25,1));
            transition-delay:.12s;
          }
          .knob-body > span.active{ opacity:1; transform:scale(1); }

          .switching .knob-body{ animation:squash .5s var(--lt-ease, cubic-bezier(.65,0,.25,1)); }
          @keyframes squash{
            0%   { transform:scale(1,1); }
            35%  { transform:scale(1.16,.86); }
            72%  { transform:scale(.94,1.05); }
            100% { transform:scale(1,1); }
          }

          @media (prefers-reduced-motion: reduce){
            *{ transition-duration:.01ms !important; animation:none !important; }
          }
        </style>

        <svg width="0" height="0" aria-hidden="true" style="position:absolute">
          <defs>
            <filter id="goo" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="${blur}" result="blur"/>
              <feColorMatrix in="blur" mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -9" result="goo"/>
              <feComposite in="SourceGraphic" in2="goo" operator="atop"/>
            </filter>
          </defs>
        </svg>

        <div class="switcher" part="switcher">
          <div class="goo" aria-hidden="true"></div>
          <div class="slots" role="tablist"></div>
          <div class="knob" aria-hidden="true"><div class="knob-body"></div></div>
        </div>
      `;

      const root     = this.shadowRoot;
      const switcher = root.querySelector(".switcher");
      const goo      = root.querySelector(".goo");
      const slotsBox = root.querySelector(".slots");
      const knobEl   = root.querySelector(".knob");
      const knobBody = root.querySelector(".knob-body");

      tabs.forEach((tab, i) => {
        const blob = document.createElement("div");
        blob.className = "blob";
        blob.style.left = pad + i * gap + "px";
        goo.appendChild(blob);

        if (i < n - 1) {
          const b = document.createElement("div");
          b.className = "blob";
          b.style.width = b.style.height = bridge + "px";
          b.style.left = pad + i * gap + r + gap / 2 - bridge / 2 + "px";
          b.style.top  = pad + r - bridge / 2 + "px";
          goo.appendChild(b);
        }

        const btn = document.createElement("button");
        btn.className = "slot";
        btn.style.left = i * gap + "px";
        btn.setAttribute("role", "tab");
        btn.setAttribute("aria-selected", i === sel ? "true" : "false");
        const lbl = document.createElement("span");
        lbl.className = "label";
        lbl.textContent = tab.label;
        btn.appendChild(lbl);
        btn.addEventListener("click", () => this.select(i));
        slotsBox.appendChild(btn);

        const knobLbl = document.createElement("span");
        knobLbl.textContent = tab.label;
        knobLbl.style.color = tab.color || "#4A5372";
        if (i === sel) knobLbl.classList.add("active");
        knobBody.appendChild(knobLbl);
      });

      const trailEl = document.createElement("div");
      trailEl.className = "blob trail";
      goo.appendChild(trailEl);

      knobEl.style.transform  = `translateX(${sel * gap}px)`;
      trailEl.style.transform = `translateX(${sel * gap}px)`;

      switcher.addEventListener("keydown", e => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        e.preventDefault();
        const next = (this._current + (e.key === "ArrowRight" ? 1 : -1) + n) % n;
        this.select(next);
        this._els.slots[next].focus();
      });

      this._els = {
        switcher,
        knob: knobEl,
        trail: trailEl,
        slots: [...slotsBox.querySelectorAll(".slot")],
        knobLabels: [...knobBody.querySelectorAll("span")]
      };

      if (this.hasAttribute("swipe")) this._bindSwipe();
    }

    // 滑動切換：拖曳時滑塊跟手、放開吸附最近的槽；快速輕撥依方向前進一格。
    // pointerdown 時把 pointer capture 到 .switcher，slot 的 click 會被吃掉，
    // 因此「未拖動的點擊」改在 pointerup 用座標推最近的槽來選取。
    _bindSwipe() {
      const els = this._els;
      const sw  = els.switcher;
      let id = null, startX = 0, startT = 0, moved = false, baseX = 0, dragX = 0;

      sw.style.touchAction = "pan-y"; // 垂直捲動留給頁面，水平手勢自己接

      const setX = (x, instant) => {
        const t = instant ? "none" : "";
        els.knob.style.transition  = t;
        els.trail.style.transition = t;
        els.knob.style.transform   = `translateX(${x}px)`;
        els.trail.style.transform  = `translateX(${x}px)`;
      };

      sw.addEventListener("pointerdown", e => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        id = e.pointerId; moved = false;
        startX = e.clientX; startT = e.timeStamp;
        baseX = this._current * this._gap;
        try { sw.setPointerCapture(id); } catch (err) { /* pointer 已失效 */ }
      });

      sw.addEventListener("pointermove", e => {
        if (e.pointerId !== id) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return; // 微小位移仍視為點擊
        moved = true;
        const max = (els.slots.length - 1) * this._gap;
        dragX = Math.max(0, Math.min(max, baseX + dx));
        setX(dragX, true);
      });

      const end = e => {
        if (e.pointerId !== id) return;
        id = null;

        // 手勢被瀏覽器接管（如轉為垂直捲動）：彈回原位、不切換
        if (e.type === "pointercancel") {
          if (moved) setX(this._current * this._gap, false);
          return;
        }

        if (!moved) {
          const rect = sw.getBoundingClientRect();
          const idx = Math.round((e.clientX - rect.left - rect.height / 2) / this._gap);
          this.select(idx); // select() 內部會 clamp 索引
          return;
        }

        setX(dragX, false); // 恢復 transition
        const dt = Math.max(1, e.timeStamp - startT);
        const v  = (e.clientX - startX) / dt; // px/ms
        let target = Math.round(dragX / this._gap);
        if (target === this._current && Math.abs(v) > 0.35) {
          target += v > 0 ? 1 : -1; // 快速輕撥：位移不到半格也依方向前進
        }
        target = Math.max(0, Math.min(els.slots.length - 1, target));
        if (target === this._current) setX(target * this._gap, false); // 彈回
        else this.select(target);
      };
      sw.addEventListener("pointerup", end);
      sw.addEventListener("pointercancel", end);
    }
  }

  customElements.define("liquid-tabs", LiquidTabs);
})();
